"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import { resolveLink } from "@/sanity/globals/CTA";

const CaretDown = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#caret-lang-clip)">
      <path
        d="M9.75 4.5L6 8.25L2.25 4.5"
        stroke="#50535A"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="caret-lang-clip">
        <rect width="12" height="12" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const FlagDE = () => (
  <svg
    width="25"
    height="18"
    viewBox="0 0 25 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="rounded-[0.3rem] flex-shrink-0 overflow-hidden">
    <rect width="25" height="6" fill="#000000" />
    <rect y="6" width="25" height="6" fill="#DD0000" />
    <rect y="12" width="25" height="6" fill="#FFCE00" />
  </svg>
);

const FlagEN = () => (
  <svg width="25" height="18" viewBox="0 0 25 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="rounded-[0.3rem] flex-shrink-0 overflow-hidden">
    {/* 13 stripes */}
    <rect y="0"    width="25" height="1.385" fill="#B22234" />
    <rect y="1.385" width="25" height="1.385" fill="#FFFFFF" />
    <rect y="2.77"  width="25" height="1.385" fill="#B22234" />
    <rect y="4.154" width="25" height="1.385" fill="#FFFFFF" />
    <rect y="5.538" width="25" height="1.385" fill="#B22234" />
    <rect y="6.923" width="25" height="1.385" fill="#FFFFFF" />
    <rect y="8.308" width="25" height="1.385" fill="#B22234" />
    <rect y="9.692" width="25" height="1.385" fill="#FFFFFF" />
    <rect y="11.077" width="25" height="1.385" fill="#B22234" />
    <rect y="12.46"  width="25" height="1.385" fill="#FFFFFF" />
    <rect y="13.846" width="25" height="1.385" fill="#B22234" />
    <rect y="15.23"  width="25" height="1.385" fill="#FFFFFF" />
    <rect y="16.615" width="25" height="1.385" fill="#B22234" />
    {/* Blue canton */}
    <rect x="0" y="0" width="10" height="9.692" fill="#3C3B6E" />
    {/* Stars (simplified dots) */}
    <circle cx="1.25" cy="1.2"  r="0.5" fill="white" /><circle cx="3.33" cy="1.2"  r="0.5" fill="white" /><circle cx="5.42" cy="1.2"  r="0.5" fill="white" /><circle cx="7.5"  cy="1.2"  r="0.5" fill="white" /><circle cx="9.58" cy="1.2"  r="0.5" fill="white" />
    <circle cx="2.29" cy="2.6"  r="0.5" fill="white" /><circle cx="4.37" cy="2.6"  r="0.5" fill="white" /><circle cx="6.46" cy="2.6"  r="0.5" fill="white" /><circle cx="8.54" cy="2.6"  r="0.5" fill="white" />
    <circle cx="1.25" cy="4.0"  r="0.5" fill="white" /><circle cx="3.33" cy="4.0"  r="0.5" fill="white" /><circle cx="5.42" cy="4.0"  r="0.5" fill="white" /><circle cx="7.5"  cy="4.0"  r="0.5" fill="white" /><circle cx="9.58" cy="4.0"  r="0.5" fill="white" />
    <circle cx="2.29" cy="5.4"  r="0.5" fill="white" /><circle cx="4.37" cy="5.4"  r="0.5" fill="white" /><circle cx="6.46" cy="5.4"  r="0.5" fill="white" /><circle cx="8.54" cy="5.4"  r="0.5" fill="white" />
    <circle cx="1.25" cy="6.8"  r="0.5" fill="white" /><circle cx="3.33" cy="6.8"  r="0.5" fill="white" /><circle cx="5.42" cy="6.8"  r="0.5" fill="white" /><circle cx="7.5"  cy="6.8"  r="0.5" fill="white" /><circle cx="9.58" cy="6.8"  r="0.5" fill="white" />
    <circle cx="2.29" cy="8.2"  r="0.5" fill="white" /><circle cx="4.37" cy="8.2"  r="0.5" fill="white" /><circle cx="6.46" cy="8.2"  r="0.5" fill="white" /><circle cx="8.54" cy="8.2"  r="0.5" fill="white" />
  </svg>
);

const FlagBR = () => (
  <svg width="25" height="18" viewBox="0 0 25 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="rounded-[0.3rem] flex-shrink-0 overflow-hidden">
    <rect width="25" height="18" fill="#009C3B" />
    <polygon points="12.5,1.8 23.2,9 12.5,16.2 1.8,9" fill="#FFDF00" />
    <circle cx="12.5" cy="9" r="4.2" fill="#002776" />
    <path d="M 8.3 9.5 Q 12.5 7.6 16.7 9.5" stroke="white" strokeWidth="1.5" fill="none" />
  </svg>
);

const LANGUAGES: Record<string, { label: string; Flag: () => JSX.Element }> = {
  en: { label: "EN", Flag: FlagEN },
  de: { label: "DE", Flag: FlagDE },
  br: { label: "BR", Flag: FlagBR },
};

function getLanguagePath(targetLang: string, currentPath: string, currentLang: string): string {
  let basePath = currentPath;
  if (currentLang && currentLang !== DEFAULT_LANGUAGE) {
    basePath = currentPath.replace(new RegExp(`^\\/${currentLang}(\\/|$)`), "/") || "/";
  }
  if (targetLang === DEFAULT_LANGUAGE) return resolveLink(basePath || "/", targetLang);
  return resolveLink(basePath === "/" ? `/${targetLang}` : `/${targetLang}${basePath}`, targetLang);
}

export default function LanguageSwitcher({
  language = DEFAULT_LANGUAGE,
  languages,
}: {
  language?: string;
  /** The languages to offer, for a page that does not exist in all of them. Default: every language. */
  languages?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [align, setAlign] = useState<'left' | 'right' | 'center'>('center');
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const current = LANGUAGES[language] ?? LANGUAGES.de;

  const entries = Object.entries(LANGUAGES).filter(([code]) => !languages || languages.includes(code));

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => {
          if (ref.current) {
            if (window.innerWidth >= 1024) {
              setAlign('center');
            } else {
              const rect = ref.current.getBoundingClientRect();
              setAlign(window.innerWidth - rect.right >= 144 ? 'left' : 'right');
            }
          }
          setOpen((v) => !v);
        }}
        className="flex items-center gap-[0.6rem] p-[0.75rem] rounded-[0.75rem] hover:bg-white/5 transition-colors duration-200"
      >
        <current.Flag />
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex-shrink-0 flex items-center"
        >
          <CaretDown />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <div
            className={cn(
              "absolute top-full mt-[0.5rem] z-50",
              align === 'center' && 'left-1/2 -translate-x-1/2',
              align === 'left' && 'left-0',
              align === 'right' && 'right-0',
            )}
          >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: -6 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            style={{
              transformOrigin:
                align === 'center' ? 'top center' : align === 'left' ? 'top left' : 'top right',
            }}
            className="bg-[#111111] border border-white/10 rounded-[0.75rem] overflow-hidden min-w-[9rem]"
          >
            {entries.map(([code, { label, Flag }], i) => (
              <motion.button
                key={code}
                initial={{ opacity: 0, x: 6 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{ x: 3 }}
                transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1], delay: i * 0.05 }}
                onClick={() => {
                  router.push(getLanguagePath(code, pathname, language));
                  setOpen(false);
                }}
                className={cn(
                  "flex items-center gap-[1.25rem] w-full px-[1.75rem] py-[1.25rem] hover:bg-white/5 transition-colors duration-200",
                  code === language && "bg-white/5",
                )}
              >
                <Flag />
                <span className="font-geist font-medium text-[1.5rem] text-white">{label}</span>
              </motion.button>
            ))}
          </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
